import React, { useState, FormEvent } from "react";
import { useNavigate, Link } from "react-router-dom";
import { signInWithEmailAndPassword, signOut } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { auth, db } from "../firebase/firebase";

/* ===================== SVG Icons ===================== */
type IconProps = { size?: number; color?: string; strokeWidth?: number };

const Svg: React.FC<IconProps & { children: React.ReactNode }> = ({
  size = 24,
  color = "currentColor",
  strokeWidth = 2,
  children,
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    {children}
  </svg>
);

const IconCart: React.FC<IconProps> = (p) => (
  <Svg {...p}>
    <circle cx="9" cy="21" r="1" />
    <circle cx="20" cy="21" r="1" />
    <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
  </Svg>
);
const IconBolt: React.FC<IconProps> = (p) => (
  <Svg {...p}>
    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
  </Svg>
);
const IconPin: React.FC<IconProps> = (p) => (
  <Svg {...p}>
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
    <circle cx="12" cy="10" r="3" />
  </Svg>
);
const IconCar: React.FC<IconProps> = (p) => (
  <Svg {...p}>
    <path d="M5 17H3v-5l2.5-6h13L21 12v5h-2" />
    <path d="M3 12h18" />
    <circle cx="7.5" cy="17" r="2" />
    <circle cx="16.5" cy="17" r="2" />
  </Svg>
);
const IconCode: React.FC<IconProps> = (p) => (
  <Svg {...p}>
    <polyline points="16 18 22 12 16 6" />
    <polyline points="8 6 2 12 8 18" />
  </Svg>
);
const IconMail: React.FC<IconProps> = (p) => (
  <Svg {...p}>
    <rect x="2" y="4" width="20" height="16" rx="2" />
    <path d="M22 6l-10 7L2 6" />
  </Svg>
);
const IconLock: React.FC<IconProps> = (p) => (
  <Svg {...p}>
    <rect x="3" y="11" width="18" height="11" rx="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </Svg>
);
const IconEye: React.FC<IconProps> = (p) => (
  <Svg {...p}>
    <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z" />
    <circle cx="12" cy="12" r="3" />
  </Svg>
);
const IconEyeOff: React.FC<IconProps> = (p) => (
  <Svg {...p}>
    <path d="M17.94 17.94A10 10 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9 9 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
    <line x1="1" y1="1" x2="23" y2="23" />
  </Svg>
);
const IconAlert: React.FC<IconProps> = (p) => (
  <Svg {...p}>
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
    <line x1="12" y1="9" x2="12" y2="13" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </Svg>
);
const IconClose: React.FC<IconProps> = (p) => (
  <Svg {...p}>
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </Svg>
);
const IconArrow: React.FC<IconProps> = (p) => (
  <Svg {...p}>
    <line x1="5" y1="12" x2="19" y2="12" />
    <polyline points="12 5 19 12 12 19" />
  </Svg>
);
const IconSpinner: React.FC<IconProps> = ({ size = 18 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    style={{ animation: "spin 0.8s linear infinite" }}
  >
    <circle
      cx="12"
      cy="12"
      r="9"
      fill="none"
      stroke="rgba(255,255,255,0.35)"
      strokeWidth="3"
    />
    <path
      d="M21 12a9 9 0 0 0-9-9"
      fill="none"
      stroke="#fff"
      strokeWidth="3"
      strokeLinecap="round"
    />
  </svg>
);

/* ===================== Landing Page ===================== */
const LandingPage: React.FC = () => {
  const [drawerOpen, setDrawerOpen] = useState(false);

  return (
    <div style={styles.page}>
      {/* ===== Header ===== */}
      <header style={styles.header}>
        <div style={styles.brand}>
          <div style={styles.brandLogo}>
            <img
              src="/logoZapee.jpeg"
              alt="Zetuka Tech"
              style={styles.brandLogoImg}
            />
          </div>
          <div>
            <div style={styles.brandName}>ZETUKA TECH</div>
            <div style={styles.brandSub}>Private Limited</div>
          </div>
        </div>

        <nav style={styles.nav}>
          <a href="#about" style={styles.navLink}>
            About
          </a>
          <a href="#services" style={styles.navLink}>
            Services
          </a>
          <a href="#team" style={styles.navLink}>
            Team
          </a>
          <a href="#contact" style={styles.navLink}>
            Contact
          </a>
          <button style={styles.loginBtn} onClick={() => setDrawerOpen(true)}>
            Login
            <IconArrow size={16} color="#fff" />
          </button>
        </nav>
      </header>

      {/* ===== Hero ===== */}
      <section style={styles.hero}>
        <div style={styles.heroContent}>
          <span style={styles.heroBadge}>
            <IconBolt size={14} color="#21808D" />
            Powering Digital Commerce
          </span>
          <h1 style={styles.heroTitle}>
            Welcome to <span style={styles.heroAccent}>Zetuka Tech</span>
            <br />
            Private Limited
          </h1>
          <p style={styles.heroText}>
            We build ecommerce, quick commerce, hyperlocal delivery, and taxi
            booking platforms — plus custom software development to power
            modern businesses end to end.
          </p>
          <div style={styles.heroActions}>
            <button
              style={styles.heroPrimary}
              onClick={() => setDrawerOpen(true)}
            >
              Login to Dashboard
            </button>
            <a href="#services" style={styles.heroSecondary}>
              Explore Services
            </a>
          </div>
        </div>
      </section>

      {/* ===== Services ===== */}
      <section id="services" style={styles.section}>
        <h2 style={styles.sectionTitle}>What We Do</h2>
        <p style={styles.sectionLead}>
          End-to-end technology for commerce, logistics, mobility, and software.
        </p>
        <div style={styles.cards}>
          {features.map((f) => (
            <div key={f.title} style={styles.card}>
              <div style={styles.cardIcon}>{f.icon}</div>
              <h3 style={styles.cardTitle}>{f.title}</h3>
              <p style={styles.cardText}>{f.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ===== About ===== */}
      <section id="about" style={styles.about}>
        <h2 style={styles.sectionTitle}>About Zetuka Tech</h2>
        <p style={styles.aboutText}>
          Zetuka Tech Private Limited is a technology company focused on building
          modern digital infrastructure for businesses. From ecommerce and
          quick commerce to hyperlocal delivery, taxi booking, and custom
          software development, we help partners run their operations
          efficiently and grow with confidence.
        </p>
        <div style={styles.stats}>
          {stats.map((s) => (
            <div key={s.label} style={styles.stat}>
              <div style={styles.statValue}>{s.value}</div>
              <div style={styles.statLabel}>{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ===== Leadership ===== */}
      <section id="team" style={styles.section}>
        <h2 style={styles.sectionTitle}>Our Leadership</h2>
        <p style={styles.sectionLead}>
          The people driving Zetuka Tech Private Limited forward.
        </p>
        <div style={styles.teamGrid}>
          {team.map((m) => (
            <div key={m.name} style={styles.teamCard}>
              <div style={styles.avatar}>{m.initials}</div>
              <h3 style={styles.teamName}>{m.name}</h3>
              <p style={styles.teamRole}>{m.role}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ===== Footer ===== */}
      <footer id="contact" style={styles.footer}>
        <div style={styles.footerBrand}>ZETUKA TECH PRIVATE LIMITED</div>
        <a href="mailto:info@zapee.in" style={styles.footerEmail}>
          info@zapee.in
        </a>
        <div style={styles.footerLinks}>
          <Link to="/privacy-policy" style={styles.footerLink}>
            Privacy Policy
          </Link>
          <Link to="/terms-conditions" style={styles.footerLink}>
            Terms &amp; Conditions
          </Link>
          <Link to="/refund-policy" style={styles.footerLink}>
            Refund Policy
          </Link>
        </div>
        <p style={styles.footerText}>
          © {new Date().getFullYear()} Zetuka Tech Private Limited. All rights
          reserved.
        </p>
      </footer>

      {/* ===== Right-side Login Drawer ===== */}
      {drawerOpen && (
        <div style={styles.overlay} onClick={() => setDrawerOpen(false)} />
      )}
      <LoginDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} />

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
};

/* ---------- Right Drawer with Login form ---------- */
const LoginDrawer: React.FC<{ open: boolean; onClose: () => void }> = ({
  open,
  onClose,
}) => {
  const [email, setEmail] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const navigate = useNavigate();

  const handleLogin = async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const credential = await signInWithEmailAndPassword(auth, email, password);
      const uid = credential.user.uid;

      // Verify shareholder document exists
      const shareSnap = await getDoc(doc(db, "zapee_share_holders", uid));
      if (!shareSnap.exists()) {
        await signOut(auth);
        setError("Access denied: No shareholder account found for this email.");
        setLoading(false);
        return;
      }

      navigate("/admin");
    } catch (err: any) {
      setError(err.message || "Failed to login");
    } finally {
      setLoading(false);
    }
  };

  return (
    <aside
      style={{
        ...styles.drawer,
        transform: open ? "translateX(0)" : "translateX(100%)",
      }}
    >
      {/* Drawer header */}
      <div style={styles.drawerHeader}>
        <div style={styles.logoSection}>
          <div style={styles.logoCircle}>
            <img
              src="/logoZapee.jpeg"
              alt="Zetuka Tech"
              style={styles.logoImg}
            />
          </div>
          <div>
            <h2 style={styles.drawerTitle}>Member Login</h2>
            <p style={styles.drawerSub}>Zetuka Tech Admin Dashboard</p>
          </div>
        </div>
        <button style={styles.closeBtn} onClick={onClose} aria-label="Close">
          <IconClose size={18} color="#475569" />
        </button>
      </div>

      <form style={styles.form} onSubmit={handleLogin}>
        {/* Email */}
        <div style={styles.inputGroup}>
          <label style={styles.label}>Email Address</label>
          <div style={styles.inputWrapper}>
            <span style={styles.inputIcon}>
              <IconMail size={18} color="#94a3b8" />
            </span>
            <input
              style={styles.input}
              placeholder="admin@zapee.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoCapitalize="none"
              autoComplete="email"
              type="email"
              required
            />
          </div>
        </div>

        {/* Password */}
        <div style={styles.inputGroup}>
          <label style={styles.label}>Password</label>
          <div style={styles.inputWrapper}>
            <span style={styles.inputIcon}>
              <IconLock size={18} color="#94a3b8" />
            </span>
            <input
              style={styles.input}
              placeholder="Enter your password"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              style={styles.eyeButton}
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? (
                <IconEyeOff size={18} color="#64748b" />
              ) : (
                <IconEye size={18} color="#64748b" />
              )}
            </button>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div style={styles.errorBox}>
            <IconAlert size={18} color="#ef4444" />
            <p style={styles.errorText}>{error}</p>
          </div>
        )}

        {/* Submit */}
        <button
          type="submit"
          style={{ ...styles.button, ...(loading ? styles.buttonDisabled : {}) }}
          disabled={loading}
        >
          {loading ? (
            <span style={styles.loadingText}>
              <IconSpinner size={18} /> Signing in...
            </span>
          ) : (
            <span style={styles.buttonText}>
              Sign In
              <IconArrow size={18} color="#fff" />
            </span>
          )}
        </button>
      </form>

      <p style={styles.drawerFooter}>Zetuka Tech Admin Panel • Secure Login</p>
    </aside>
  );
};

const features = [
  {
    icon: <IconCart size={24} color="#21808D" />,
    title: "Ecommerce",
    text: "Full-featured online stores with catalogs, carts, and secure checkout.",
  },
  {
    icon: <IconBolt size={24} color="#21808D" />,
    title: "Quick Commerce",
    text: "Ultra-fast delivery of everyday essentials in just minutes.",
  },
  {
    icon: <IconPin size={24} color="#21808D" />,
    title: "Hyperlocal Delivery",
    text: "Connecting local stores and customers with on-demand logistics.",
  },
  {
    icon: <IconCar size={24} color="#21808D" />,
    title: "Taxi Booking",
    text: "Ride-hailing platforms with live tracking and fare management.",
  },
  {
    icon: <IconCode size={24} color="#21808D" />,
    title: "Software Development",
    text: "Custom web, mobile, and cloud software built to scale.",
  },
];

const team = [
  { name: "Abdul Awal", role: "Founder", initials: "AA" },
  { name: "Hafizul Islam", role: "Chief Executive Officer", initials: "HI" },
  {
    name: "Advocate Nehrur Islam",
    role: "Managing Director",
    initials: "NI",
  },
  { name: "Shoriful Islam", role: "Director", initials: "SI" },
];

const stats = [
  { value: "10K+", label: "Orders Processed" },
  { value: "500+", label: "Partners" },
  { value: "99.9%", label: "Uptime" },
  { value: "24/7", label: "Support" },
];

const styles: { [key: string]: React.CSSProperties } = {
  page: {
    minHeight: "100vh",
    width: "100%",
    margin: 0,
    fontFamily:
      "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    color: "#1a2332",
    backgroundColor: "#ffffff",
  },

  /* Header */
  header: {
    position: "sticky",
    top: 0,
    zIndex: 50,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "14px 32px",
    backgroundColor: "rgba(255,255,255,0.95)",
    backdropFilter: "blur(10px)",
    borderBottom: "1px solid #e2e8f0",
  },
  brand: { display: "flex", alignItems: "center", gap: 12 },
  brandLogo: {
    width: 40,
    height: 40,
    borderRadius: 8,
    overflow: "hidden",
    border: "1px solid #e2e8f0",
    flexShrink: 0,
  },
  brandLogoImg: { width: "100%", height: "100%", objectFit: "cover" },
  brandName: {
    fontSize: 16,
    fontWeight: 700,
    letterSpacing: 1,
    color: "#1a2332",
  },
  brandSub: { fontSize: 11, color: "#64748b", letterSpacing: 1 },
  nav: { display: "flex", alignItems: "center", gap: 24 },
  navLink: {
    fontSize: 14,
    fontWeight: 600,
    color: "#475569",
    textDecoration: "none",
  },
  loginBtn: {
    height: 40,
    padding: "0 18px",
    backgroundColor: "#21808D",
    color: "#fff",
    border: "none",
    borderRadius: 6,
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: 8,
  },

  /* Hero */
  hero: {
    backgroundColor: "#ffffff",
    color: "#1a2332",
    padding: "80px 32px",
    display: "flex",
    justifyContent: "center",
    borderBottom: "1px solid #e2e8f0",
  },
  heroContent: { maxWidth: 760, textAlign: "center" },
  heroBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "6px 14px",
    borderRadius: 6,
    backgroundColor: "#e6f2f3",
    color: "#21808D",
    fontSize: 13,
    fontWeight: 600,
    marginBottom: 22,
  },
  heroTitle: {
    fontSize: 42,
    fontWeight: 700,
    lineHeight: 1.2,
    margin: "0 0 20px 0",
    color: "#1a2332",
  },
  heroAccent: { color: "#21808D" },
  heroText: {
    fontSize: 17,
    lineHeight: 1.6,
    color: "#64748b",
    margin: "0 0 32px 0",
  },
  heroActions: {
    display: "flex",
    gap: 14,
    justifyContent: "center",
    flexWrap: "wrap",
  },
  heroPrimary: {
    height: 48,
    padding: "0 26px",
    backgroundColor: "#21808D",
    color: "#fff",
    border: "none",
    borderRadius: 6,
    fontSize: 15,
    fontWeight: 600,
    cursor: "pointer",
  },
  heroSecondary: {
    height: 48,
    padding: "0 26px",
    display: "flex",
    alignItems: "center",
    color: "#1a2332",
    border: "1px solid #cbd5e1",
    borderRadius: 6,
    fontSize: 15,
    fontWeight: 600,
    textDecoration: "none",
  },

  /* Sections */
  section: { padding: "72px 32px", maxWidth: 1100, margin: "0 auto" },
  sectionTitle: {
    fontSize: 28,
    fontWeight: 700,
    textAlign: "center",
    margin: "0 0 10px 0",
    color: "#1a2332",
  },
  sectionLead: {
    fontSize: 15,
    color: "#64748b",
    textAlign: "center",
    margin: "0 0 44px 0",
  },
  cards: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))",
    gap: 20,
  },
  card: {
    backgroundColor: "#ffffff",
    border: "1px solid #e2e8f0",
    borderRadius: 8,
    padding: "26px 22px",
    textAlign: "center",
  },
  cardIcon: {
    width: 48,
    height: 48,
    borderRadius: 8,
    backgroundColor: "#e6f2f3",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "0 auto 16px",
  },
  cardTitle: { fontSize: 16, fontWeight: 700, margin: "0 0 8px 0" },
  cardText: { fontSize: 14, color: "#64748b", lineHeight: 1.55, margin: 0 },

  /* Team */
  teamGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))",
    gap: 20,
  },
  teamCard: {
    backgroundColor: "#ffffff",
    border: "1px solid #e2e8f0",
    borderRadius: 8,
    padding: "30px 22px",
    textAlign: "center",
  },
  avatar: {
    width: 64,
    height: 64,
    borderRadius: "50%",
    backgroundColor: "#21808D",
    color: "#fff",
    fontSize: 22,
    fontWeight: 700,
    letterSpacing: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "0 auto 14px",
  },
  teamName: { fontSize: 16, fontWeight: 700, margin: "0 0 4px 0" },
  teamRole: { fontSize: 13, color: "#21808D", fontWeight: 600, margin: 0 },

  /* About */
  about: {
    backgroundColor: "#f8fafc",
    padding: "72px 32px",
    textAlign: "center",
    borderTop: "1px solid #e2e8f0",
    borderBottom: "1px solid #e2e8f0",
  },
  aboutText: {
    maxWidth: 720,
    margin: "0 auto 44px",
    fontSize: 16,
    lineHeight: 1.7,
    color: "#475569",
  },
  stats: {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "center",
    gap: 48,
  },
  stat: { textAlign: "center" },
  statValue: {
    fontSize: 30,
    fontWeight: 700,
    color: "#21808D",
  },
  statLabel: { fontSize: 13, color: "#64748b", marginTop: 4 },

  /* Footer */
  footer: {
    backgroundColor: "#1a2332",
    color: "#fff",
    textAlign: "center",
    padding: "36px 32px",
  },
  footerBrand: {
    fontSize: 14,
    fontWeight: 700,
    letterSpacing: 1,
    marginBottom: 8,
  },
  footerEmail: {
    display: "inline-block",
    fontSize: 13,
    color: "#7dd3c8",
    textDecoration: "none",
    fontWeight: 600,
    marginBottom: 14,
  },
  footerLinks: {
    display: "flex",
    justifyContent: "center",
    gap: 28,
    flexWrap: "wrap",
    marginBottom: 14,
  },
  footerLink: {
    fontSize: 13,
    color: "rgba(255,255,255,0.85)",
    textDecoration: "none",
    fontWeight: 600,
  },
  footerText: { fontSize: 13, color: "rgba(255,255,255,0.6)", margin: 0 },

  /* Drawer */
  overlay: {
    position: "fixed",
    inset: 0,
    backgroundColor: "rgba(0,0,0,0.5)",
    zIndex: 90,
  },
  drawer: {
    position: "fixed",
    top: 0,
    right: 0,
    height: "100vh",
    width: 400,
    maxWidth: "100vw",
    backgroundColor: "#fff",
    boxShadow: "-6px 0 24px rgba(0,0,0,0.12)",
    zIndex: 100,
    transition: "transform 0.28s ease",
    padding: "28px 32px",
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
    overflowY: "auto",
  },
  drawerHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    marginBottom: 32,
  },
  logoSection: { display: "flex", alignItems: "center", gap: 12 },
  logoCircle: {
    width: 46,
    height: 46,
    borderRadius: 8,
    overflow: "hidden",
    border: "1px solid #e2e8f0",
    flexShrink: 0,
  },
  logoImg: { width: "100%", height: "100%", objectFit: "cover" },
  drawerTitle: { fontSize: 18, fontWeight: 700, color: "#1a2332", margin: 0 },
  drawerSub: { fontSize: 12, color: "#64748b", margin: "2px 0 0 0" },
  closeBtn: {
    background: "#f1f5f9",
    border: "1px solid #e2e8f0",
    borderRadius: 6,
    width: 34,
    height: 34,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
  },
  form: { display: "flex", flexDirection: "column", gap: 20 },
  inputGroup: { display: "flex", flexDirection: "column", gap: 8 },
  label: { fontSize: 13, fontWeight: 600, color: "#374151" },
  inputWrapper: { position: "relative", display: "flex", alignItems: "center" },
  inputIcon: {
    position: "absolute",
    left: 14,
    display: "flex",
    alignItems: "center",
    zIndex: 1,
  },
  input: {
    width: "100%",
    height: 46,
    paddingLeft: 42,
    paddingRight: 42,
    borderRadius: 6,
    border: "1px solid #cbd5e1",
    fontSize: 15,
    color: "#1a2332",
    backgroundColor: "#fff",
    boxSizing: "border-box",
    outline: "none",
  },
  eyeButton: {
    position: "absolute",
    right: 12,
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: 4,
    display: "flex",
    alignItems: "center",
  },
  errorBox: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    backgroundColor: "#fef2f2",
    borderRadius: 6,
    padding: "12px 14px",
    border: "1px solid #fee2e2",
  },
  errorText: { margin: 0, fontSize: 13, color: "#ef4444", fontWeight: 500 },
  button: {
    height: 48,
    backgroundColor: "#21808D",
    borderRadius: 6,
    border: "none",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginTop: 4,
  },
  buttonDisabled: { opacity: 0.7, cursor: "not-allowed" },
  buttonText: {
    color: "white",
    fontWeight: 600,
    fontSize: 15,
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  loadingText: {
    color: "white",
    fontSize: 15,
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  drawerFooter: {
    textAlign: "center",
    fontSize: 12,
    color: "#94a3b8",
    marginTop: 28,
    marginBottom: 0,
  },
};

export default LandingPage;
